import { type Curves } from "@taquito/signer";
import {
  type ChainId,
  type ImplicitAccount,
  type PasskeyAccount,
  defaultDerivationPathTemplates,
} from "@umami/chains";
import { DEFAULT_ACCOUNT_LABEL } from "@umami/core";
import { decrypt, encrypt } from "@umami/crypto";
import { authenticatePasskey, getIsPasskeySupported, usePasskeyRegistration } from "@umami/passkey";
import {
  accountsActions,
  generate24WordMnemonic,
  useAppDispatch,
  useAppSelector,
  useCreateMnemonicWallets,
  useCreateSecretKeyWallets,
  useGetDefaultAccount,
  useGetNextAvailableAccountLabels,
  useHandleSession,
  useImplicitAccounts,
  useRestoreFromSecretKey,
} from "@umami/state";
import { isMultichainEnabled } from "@umami/utils";
import { useEffect, useState } from "react";

import { getDefaultAccount } from "../utils/accounts";
import { getIsPasskeyFlagEnabled, getPasskeyApiUrl } from "../utils/featureFlag";
import { getPersistedPasskeyRotationStatus, setupPersistence } from "../utils/store";

type CreateMnemonicWallet = {
  mnemonic?: string;
  label?: string;
  derivationPaths?: Record<ChainId, string>;
  curve?: Curves;
};

export type RegisterFn = () => Promise<
  | {
      verified: true;
      userId: string;
      dek: string;
      passkey_kek: string;
    }
  | {
      verified: false;
      userId?: undefined;
      dek?: undefined;
      passkey_kek?: undefined;
    }
>;
const getIsPasskeyAccount = (account: ImplicitAccount | null): account is PasskeyAccount => {
  if (!account) {
    return false;
  } else {
    return "encrypted_dek" in account;
  }
};

export const usePasskey = () => {
  const apiUrl = getPasskeyApiUrl();
  const { register: registerPasskey } = usePasskeyRegistration({ apiUrl });
  const createMnemonicWallets = useCreateMnemonicWallets();
  const createSecretKeyWallets = useCreateSecretKeyWallets();
  const dispatch = useAppDispatch();
  const getNextAvailableAccountLabels = useGetNextAvailableAccountLabels();
  const { setupSessionTimeout, isSessionActive } = useHandleSession();
  const restoreFromSecretKey = useRestoreFromSecretKey();
  const implicitAccounts = useImplicitAccounts();
  const defaultAccount = useGetDefaultAccount();
  const isDefaultAccountSocial = defaultAccount?.type === "social";
  const doesPasskeyAccountExist = implicitAccounts.some(account => getIsPasskeyAccount(account));

  const [isPasskeySupported, setIsPasskeySupported] = useState(false);
  const isPasskeyFlagEnabled = getIsPasskeyFlagEnabled();
  const isPasskeyUser = useAppSelector(state => state.accounts.isPasskeyUser);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const setIsSupported = async () => {
      const isSupported = await getIsPasskeySupported();
      setIsPasskeySupported(isSupported);
    };
    void setIsSupported();
  }, []);

  const register = async () => {
    setIsLoading(true);
    try {
      const response = await registerPasskey();

      if (response?.verified) {
        const dekBytes = crypto.getRandomValues(new Uint8Array(32));
        const dek = Array.from(dekBytes, byte => byte.toString(16).padStart(2, "0")).join("");

        return {
          verified: response.verified,
          dek,
          userId: response.userId,
          passkey_kek: response.passkey_kek,
        };
      } else {
        return {
          verified: false as const,
          userId: undefined,
          dek: undefined,
          passkey_kek: undefined,
        };
      }
    } catch (error) {
      console.error("PASSKEY: Registration failed with error:", error);

      return {
        verified: false as const,
        userId: undefined,
        dek: undefined,
        passkey_kek: undefined,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const authenticate = async () => {
    setIsLoading(true);
    try {
      const shouldUseDefaultAccount = !isDefaultAccountSocial;
      const firstPasskeyAccount = implicitAccounts.find(account =>
        getIsPasskeyAccount(account)
      ) as PasskeyAccount;
      const account = shouldUseDefaultAccount ? getDefaultAccount() : firstPasskeyAccount;
      const isPasskeyAccount = getIsPasskeyAccount(account);

      if (!isPasskeyAccount) {
        throw new Error("account is not a passkey account");
      }

      let rotationStatus = getPersistedPasskeyRotationStatus();
      const { passkey_kek, next_passkey_kek, verified } = await authenticatePasskey({
        apiUrl,
        userId: account.userId,
        keyRotationStatus: rotationStatus,
      });

      if (verified) {
        if (rotationStatus !== "Inactive") {
          rotationStatus = "Inactive";
          dispatch(accountsActions.setPasskeyRotationStatus({ status: rotationStatus }));
        }
        const dek = await decrypt(account.encrypted_dek, passkey_kek);

        let updated_passkey_kek = passkey_kek;

        // Perform rotation if a next key is provided
        if (next_passkey_kek && next_passkey_kek.length > 0) {
          try {
            const newEncryptedDek = await encrypt(dek, next_passkey_kek);

            if (isSessionActive) {
              dispatch(
                accountsActions.setEncryptedDek({
                  pkh: account.address.pkh,
                  encrypted_dek: newEncryptedDek,
                })
              );
              dispatch(accountsActions.clearNewEncryptedDek());
            } else {
              dispatch(
                accountsActions.setNewEncryptedDek({
                  rawPkh: account.address.pkh,
                  encrypted_dek: newEncryptedDek,
                })
              );
            }
            dispatch(accountsActions.setPasskeyRotationStatus({ status: "Success" }));

            updated_passkey_kek = next_passkey_kek;
          } catch (e) {
            console.error("PASSKEY: Key rotation failed", e);
            dispatch(accountsActions.setPasskeyRotationStatus({ status: "Failure" }));
          }
        }

        return {
          dek,
          passkey_kek: updated_passkey_kek,
          userId: account.userId,
          verified,
        };
      } else {
        throw new Error("authentication not verified");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const createMnemonicWallet = async (
    {
      mnemonic = generate24WordMnemonic(),
      label = getNextAvailableAccountLabels(DEFAULT_ACCOUNT_LABEL)[0],
      derivationPaths,
      curve = "secp256k1" as Curves,
    }: CreateMnemonicWallet = {
      mnemonic: generate24WordMnemonic(),
    }
  ) => {
    setIsLoading(true);
    try {
      const shouldAuthenticate = doesPasskeyAccountExist;
      const { verified, dek, passkey_kek, userId } = shouldAuthenticate
        ? await authenticate()
        : await register();

      if (verified) {
        const accounts = await createMnemonicWallets({
          mnemonic,
          password: dek,
          derivationPaths,
          derivationPathsTemplates: defaultDerivationPathTemplates,
          label,
          curve,
          isVerified: false,
          encrypted_dek: await encrypt(dek, passkey_kek),
          userId,
        });

        dispatch(accountsActions.setCurrent(accounts[0].address.pkh));

        if (!shouldAuthenticate) {
          dispatch(accountsActions.setIsPasskeyUser(true));
        }

        if (!isSessionActive) {
          await setupPersistence(mnemonic);
          setupSessionTimeout();
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const createSecretKeyWallet = async (secretKey: string) => {
    setIsLoading(true);
    try {
      const { verified, dek, userId, passkey_kek } = doesPasskeyAccountExist
        ? await authenticate()
        : await register();

      if (verified) {
        const password = dek;
        const account = isMultichainEnabled()
          ? await createSecretKeyWallets({
              secretKey,
              password: dek,
              label: DEFAULT_ACCOUNT_LABEL,
              encrypted_dek: await encrypt(dek, passkey_kek),
              userId,
            })
          : await restoreFromSecretKey(
              secretKey,
              password,
              DEFAULT_ACCOUNT_LABEL,
              await encrypt(dek, passkey_kek),
              userId
            );

        dispatch(accountsActions.setCurrent(account.address.pkh));

        if (!doesPasskeyAccountExist) {
          dispatch(accountsActions.setIsPasskeyUser(true));
        }

        if (!isSessionActive) {
          await setupPersistence(secretKey);
          setupSessionTimeout();
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    register,
    authenticate,
    isPasskeyFlow: isPasskeySupported && isPasskeyFlagEnabled,
    isPasskeyUser,
    getIsPasskeyAccount,
    createMnemonicWallet,
    createSecretKeyWallet,
    isLoading,
  };
};
